import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { geocodeAddress } from '../services/geocodingService';

interface OpeningHours {
  open: string;
  close: string;
  closed: boolean;
}

interface Venue {
  id: string;
  name: string;
  postalCode: string;
  city: string;
  street: string;
  houseNumber: string;
  address: string;
  latitude: number;
  longitude: number;
  pricePerHour: number;
  openingHours: {
    monday: OpeningHours;
    tuesday: OpeningHours;
    wednesday: OpeningHours;
    thursday: OpeningHours;
    friday: OpeningHours;
    saturday: OpeningHours;
    sunday: OpeningHours;
  };
}

interface PendingRequest {
  id: string;
  venueName: string;
  location: string;
  status: string;
  createdAt: any;
}

const VenueOperation: React.FC = () => {
  const { t } = useTranslation();
  const { user, isVenueOperator } = useAuth();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingVenue, setEditingVenue] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [deletingVenue, setDeletingVenue] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    postalCode: '',
    city: '',
    street: '',
    houseNumber: '',
    pricePerHour: 0,
    openingHours: {
      monday: { open: '09:00', close: '22:00', closed: false },
      tuesday: { open: '09:00', close: '22:00', closed: false },
      wednesday: { open: '09:00', close: '22:00', closed: false },
      thursday: { open: '09:00', close: '22:00', closed: false },
      friday: { open: '09:00', close: '23:00', closed: false },
      saturday: { open: '10:00', close: '23:00', closed: false },
      sunday: { open: '10:00', close: '20:00', closed: false }
    }
  });

  useEffect(() => {
    if (user && isVenueOperator) {
      loadVenues();
      loadPendingRequests();
    }
  }, [user, isVenueOperator]);

  const loadVenues = async () => {
    if (!user?.uid) return;

    setLoading(true);
    try {
      const venuesQuery = query(
        collection(db, 'venues'),
        where('operatorId', '==', user.uid),
        where('isActive', '==', true)
      );
      const venuesSnapshot = await getDocs(venuesQuery);

      const venuesData: Venue[] = [];
      venuesSnapshot.forEach((doc) => {
        const data = doc.data();
        venuesData.push({
          id: doc.id,
          name: data.name || '',
          postalCode: data.postalCode || '',
          city: data.city || '',
          street: data.street || '',
          houseNumber: data.houseNumber || '',
          address: data.address || '',
          latitude: data.latitude || 0,
          longitude: data.longitude || 0,
          pricePerHour: data.pricePerHour || 0,
          openingHours: data.openingHours || {
            monday: { open: '09:00', close: '22:00', closed: false },
            tuesday: { open: '09:00', close: '22:00', closed: false },
            wednesday: { open: '09:00', close: '22:00', closed: false },
            thursday: { open: '09:00', close: '22:00', closed: false },
            friday: { open: '09:00', close: '23:00', closed: false },
            saturday: { open: '10:00', close: '23:00', closed: false },
            sunday: { open: '10:00', close: '20:00', closed: false }
          }
        });
      });

      setVenues(venuesData);
    } catch (error) {
      console.error('Error loading venues:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    if (!user?.uid) return;

    try {
      const requestsQuery = query(
        collection(db, 'venueRequests'),
        where('userId', '==', user.uid),
        where('status', '==', 'pending')
      );
      const requestsSnapshot = await getDocs(requestsQuery);

      const requestsData: PendingRequest[] = [];
      requestsSnapshot.forEach((doc) => {
        const data = doc.data();
        requestsData.push({
          id: doc.id,
          venueName: data.venueName || '',
          location: data.location || '',
          status: data.status || 'pending',
          createdAt: data.createdAt
        });
      });

      setPendingRequests(requestsData);
    } catch (error) {
      console.error('Error loading pending requests:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name || !formData.postalCode || !formData.city || !formData.street || !formData.houseNumber) {
      alert(t('venueOperation.allFieldsRequired'));
      return;
    }

    if (!formData.pricePerHour || formData.pricePerHour <= 0) {
      alert(t('venueOperation.priceRequired'));
      return;
    }

    setSaving(true);
    try {
      // Get GPS coordinates automatically
      const location = await geocodeAddress(
        formData.postalCode,
        formData.city,
        formData.street,
        formData.houseNumber
      );

      // Prepare venue data
      const venueData = {
        name: formData.name,
        postalCode: formData.postalCode,
        city: formData.city,
        street: formData.street,
        houseNumber: formData.houseNumber,
        address: `${formData.street} ${formData.houseNumber}.`,
        latitude: location.latitude,
        longitude: location.longitude,
        pricePerHour: formData.pricePerHour,
        openingHours: formData.openingHours,
        operatorId: user?.uid,
        isActive: true,
        status: 'active',
        createdAt: serverTimestamp()
      };

      if (editingVenue) {
        // Update existing venue
        await updateDoc(doc(db, 'venues', editingVenue), {
          ...venueData,
          updatedAt: serverTimestamp()
        });
        alert(t('venueOperation.venueUpdated'));
      } else {
        // Check if operator already has venues
        if (venues.length > 0) {
          // Create venue request for admin approval
          await addDoc(collection(db, 'venueRequests'), {
            userId: user?.uid,
            venueName: formData.name,
            location: `${formData.postalCode} ${formData.city}, ${formData.street} ${formData.houseNumber}.`,
            phone: user?.phoneNumber || '',
            status: 'pending',
            venueData: venueData,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
          alert(t('venueOperation.venueRequestSent'));
          await loadPendingRequests();
        } else {
          // First venue - create directly
          await addDoc(collection(db, 'venues'), venueData);
          alert(t('venueOperation.venueCreated'));
        }
      }

      // Reset form
      setFormData({
        name: '',
        postalCode: '',
        city: '',
        street: '',
        houseNumber: '',
        pricePerHour: 0,
        openingHours: {
          monday: { open: '09:00', close: '22:00', closed: false },
          tuesday: { open: '09:00', close: '22:00', closed: false },
          wednesday: { open: '09:00', close: '22:00', closed: false },
          thursday: { open: '09:00', close: '22:00', closed: false },
          friday: { open: '09:00', close: '23:00', closed: false },
          saturday: { open: '10:00', close: '23:00', closed: false },
          sunday: { open: '10:00', close: '20:00', closed: false }
        }
      });
      setShowAddModal(false);
      setEditingVenue(null);
      await loadVenues();
    } catch (error: any) {
      console.error('Error saving venue:', error);
      alert('Hiba: ' + (error.message || 'Ismeretlen hiba történt'));
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (venue: Venue) => {
    setEditingVenue(venue.id);
    setFormData({
      name: venue.name,
      postalCode: venue.postalCode,
      city: venue.city,
      street: venue.street,
      houseNumber: venue.houseNumber,
      pricePerHour: venue.pricePerHour,
      openingHours: venue.openingHours
    });
    setShowAddModal(true);
  };

  const cancelEditing = () => {
    setEditingVenue(null);
    setShowAddModal(false);
    setFormData({
      name: '',
      postalCode: '',
      city: '',
      street: '',
      houseNumber: '',
      pricePerHour: 0,
      openingHours: {
        monday: { open: '09:00', close: '22:00', closed: false },
        tuesday: { open: '09:00', close: '22:00', closed: false },
        wednesday: { open: '09:00', close: '22:00', closed: false },
        thursday: { open: '09:00', close: '22:00', closed: false },
        friday: { open: '09:00', close: '23:00', closed: false },
        saturday: { open: '10:00', close: '23:00', closed: false },
        sunday: { open: '10:00', close: '20:00', closed: false }
      }
    });
  };

  const deleteVenue = async (venueId: string) => {
    if (!window.confirm(t('venueOperation.confirmDelete'))) {
      return;
    }

    setDeletingVenue(venueId);
    try {
      // Delete all boards associated with this venue
      const boardsQuery = query(
        collection(db, 'boards'),
        where('venueId', '==', venueId)
      );
      const boardsSnapshot = await getDocs(boardsQuery);

      const deletePromises = boardsSnapshot.docs.map(boardDoc =>
        updateDoc(doc(db, 'boards', boardDoc.id), { isActive: false })
      );
      await Promise.all(deletePromises);

      // Soft delete venue (set isActive to false)
      await updateDoc(doc(db, 'venues', venueId), {
        isActive: false,
        status: 'deleted',
        updatedAt: serverTimestamp()
      });

      await loadVenues();
      alert(t('venueOperation.venueDeleted'));
    } catch (error) {
      console.error('Error deleting venue:', error);
      alert(t('venueOperation.deleteError'));
    } finally {
      setDeletingVenue(null);
    }
  };

  if (!isVenueOperator) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card-dark p-8">
          <div className="text-center py-8">
            <p className="text-[#B8B8C3]">{t('venueOperation.notAuthorized')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="card-dark p-8">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      <div className="card-dark p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-3xl font-bold text-gradient-cyan mb-2">{t('venueOperation.title')}</h2>
            <p className="text-[#B8B8C3]">{t('venueOperation.description')}</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all"
          >
            + {t('venueOperation.addVenue')}
          </button>
        </div>

        {/* Pending Requests */}
        {pendingRequests.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">🕒 {t('venueOperation.pendingRequests')}</h3>
            <div className="space-y-3">
              {pendingRequests.map((request) => (
                <div key={request.id} className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-500/20 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-semibold">{request.venueName}</p>
                      <p className="text-sm text-[#B8B8C3]">{request.location}</p>
                    </div>
                    <span className="px-3 py-1 bg-yellow-500/20 text-yellow-500 text-xs font-semibold rounded-full">
                      {t('venueOperation.processing')}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {venues.length === 0 && pendingRequests.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-[#15191E] rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-[#B8B8C3]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">{t('venueOperation.noVenues')}</h3>
            <p className="text-[#B8B8C3] mb-4">{t('venueOperation.noVenuesDesc')}</p>
          </div>
        ) : venues.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {venues.map((venue) => (
              <div key={venue.id} className="bg-[#1d232a] rounded-lg p-6 border border-[#31383D]">
                <h3 className="text-xl font-bold text-white mb-3">{venue.name}</h3>
                <div className="space-y-2 text-sm text-[#B8B8C3] mb-4">
                  <p><strong>{t('venueOperation.address')}:</strong> {venue.postalCode} {venue.city}, {venue.street} {venue.houseNumber}.</p>
                  <p><strong>{t('venueOperation.gps')}:</strong> {venue.latitude.toFixed(4)}, {venue.longitude.toFixed(4)}</p>
                  <p><strong>{t('venueOperation.pricePerHour')}:</strong> {venue.pricePerHour} Ft</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => startEditing(venue)}
                    className="flex-1 px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => deleteVenue(venue.id)}
                    disabled={deletingVenue === venue.id}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                  >
                    {deletingVenue === venue.id ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="card-dark rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">
              {editingVenue ? t('venueOperation.editVenue') : t('venueOperation.addNewVenue')}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('venueOperation.venueName')} {t('venueOperation.required')}
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  placeholder={t('venueOperation.venueNamePlaceholder')}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('venueOperation.postalCode')} {t('venueOperation.required')}
                  </label>
                  <input
                    type="text"
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    placeholder={t('venueOperation.postalCodePlaceholder')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('venueOperation.city')} {t('venueOperation.required')}
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    placeholder={t('venueOperation.cityPlaceholder')}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('venueOperation.street')} {t('venueOperation.required')}
                  </label>
                  <input
                    type="text"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    placeholder={t('venueOperation.streetPlaceholder')}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                    {t('venueOperation.houseNumber')} {t('venueOperation.required')}
                  </label>
                  <input
                    type="text"
                    value={formData.houseNumber}
                    onChange={(e) => setFormData({ ...formData, houseNumber: e.target.value })}
                    className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    placeholder={t('venueOperation.houseNumberPlaceholder')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#B8B8C3] mb-2">
                  {t('venueOperation.pricePerHour')} {t('venueOperation.required')}
                </label>
                <input
                  type="number"
                  value={formData.pricePerHour || ''}
                  onChange={(e) => setFormData({ ...formData, pricePerHour: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 bg-[#15191E] border border-[#31383D] rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  placeholder={t('venueOperation.pricePerHourPlaceholder')}
                  required
                />
              </div>

              <div className="border-t border-[#31383D] pt-4">
                <h4 className="text-sm font-medium text-white mb-3">{t('venueOperation.openingHours')}</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                    const hours = formData.openingHours[day as keyof typeof formData.openingHours];
                    return (
                      <div key={day} className="grid grid-cols-4 gap-2 items-center">
                        <label className="text-xs text-[#B8B8C3]">{t(`venueOperation.days.${day}`)}</label>
                        <input
                          type="time"
                          value={hours.open}
                          onChange={(e) => setFormData({
                            ...formData,
                            openingHours: {
                              ...formData.openingHours,
                              [day]: { ...hours, open: e.target.value }
                            }
                          })}
                          disabled={hours.closed}
                          className="px-2 py-1 text-xs border border-[#31383D] rounded bg-[#010306] text-white disabled:opacity-50"
                        />
                        <input
                          type="time"
                          value={hours.close}
                          onChange={(e) => setFormData({
                            ...formData,
                            openingHours: {
                              ...formData.openingHours,
                              [day]: { ...hours, close: e.target.value }
                            }
                          })}
                          disabled={hours.closed}
                          className="px-2 py-1 text-xs border border-[#31383D] rounded bg-[#010306] text-white disabled:opacity-50"
                        />
                        <label className="flex items-center gap-1 text-xs text-[#B8B8C3]">
                          <input
                            type="checkbox"
                            checked={hours.closed}
                            onChange={(e) => setFormData({
                              ...formData,
                              openingHours: {
                                ...formData.openingHours,
                                [day]: { ...hours, closed: e.target.checked }
                              }
                            })}
                            className="rounded"
                          />
                          {t('venueOperation.closed')}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-lg hover:shadow-[0_0_20px_rgba(6,182,212,0.5)] transition-all disabled:opacity-50"
                >
                  {saving ? t('common.saving') : (editingVenue ? t('venueOperation.saveChanges') : t('venueOperation.addNewVenue'))}
                </button>
                <button
                  type="button"
                  onClick={cancelEditing}
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-[#31383D] text-white rounded-lg hover:bg-[#3d4758] transition-colors disabled:opacity-50"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VenueOperation;
